import { z } from "zod/v4";

import {
  SubscriptionBillingProviderList,
  SubscriptionIntervalList,
  SubscriptionWithScopesDtoSchema,
} from "./subscriptions";

export const SubscriptionStatusList = [
  "active",
  "pending",
  "cancelled",
] as const;
export type SubscriptionStatus = (typeof SubscriptionStatusList)[number];

export const StripeSubscriptionStatusList = [
  "active",
  "canceled",
  "cancelled",
  "pending",
  "incomplete",
  "incomplete_expired",
  "past_due",
  "paused",
  "trialing",
  "unpaid",
] as const;

export type StripeSubscriptionStatus =
  (typeof StripeSubscriptionStatusList)[number];

/**
 * Stripe statuses that represent a live, actionable subscription, ordered by
 * descending tiebreak priority (active > trialing > past_due) for callers that
 * must pick one primary among several live subs. "live" is the union, not the
 * literal "active": a check that only matches "active" silently ignores
 * trialing and past_due subs that are still actionable. `satisfies` guards
 * against status-id typos.
 */
export const LIVE_SUBSCRIPTION_STATUSES = [
  "active",
  "trialing",
  "past_due",
] as const satisfies readonly StripeSubscriptionStatus[];

export type LiveSubscriptionStatus =
  (typeof LIVE_SUBSCRIPTION_STATUSES)[number];

export function isLiveSubscriptionStatus(
  status: string | null | undefined
): status is LiveSubscriptionStatus {
  return (LIVE_SUBSCRIPTION_STATUSES as readonly string[]).includes(
    status ?? ""
  );
}

/**
 * Stripe statuses that represent a terminal (dead) subscription — one that will
 * never bill again. `satisfies` guards against status-id typos.
 */
export const TERMINAL_SUBSCRIPTION_STATUSES = [
  "canceled",
  "cancelled",
  "incomplete_expired",
  "unpaid",
] as const satisfies readonly StripeSubscriptionStatus[];

/**
 * True when a subscription is still "in flight" — present and not terminal.
 * Unlike `isLiveSubscriptionStatus` (which matches only active/trialing/past_due),
 * this also counts subscriptions that are not yet live but not dead — notably
 * `pending` (awaiting external/Shopify approval), `paused`, and `incomplete`. A
 * null/absent status is treated as not in flight. Callers use this to decide
 * whether an org still has an obligation on a subscription, rather than whether
 * that subscription is currently billable.
 */
export function isInFlightSubscriptionStatus(
  status: string | null | undefined
): boolean {
  return (
    status != null &&
    status !== "" &&
    !(TERMINAL_SUBSCRIPTION_STATUSES as readonly string[]).includes(status)
  );
}

/** The catalog version that marks a subscription/price as modular_2026. */
export const MODULAR_CATALOG_VERSION = "2026_module_v1";

/** The billing regime that puts an org unconditionally on modular_2026. */
export const MODULAR_BILLING_REGIME = "modular_2026";

/**
 * The single source of truth for whether an organization belongs on the
 * modular_2026 billing surface. modular_2026 is the go-forward regime, so an
 * org whose regime has not been set (null/undefined) defaults to modular UNLESS
 * it still has an in-flight legacy obligation — an active or pending non-modular
 * subscription, or an active legacy subscription invisible to the cached list.
 * An explicit non-modular regime is always authoritative.
 *
 * Callers that already hold a computed `org.isModular` from the backend should
 * read that field instead of recomputing; this function exists for the sites
 * that must derive the answer from raw subscription data (the backend response
 * builder and the admin organization summary table).
 */
export function isModularBillingOrg(input: {
  billingRegime?: string | null;
  subscriptions: ReadonlyArray<{
    status?: string | null;
    version?: string | null;
  }>;
  hasActiveLegacySubscription?: boolean;
}): boolean {
  const { billingRegime, subscriptions, hasActiveLegacySubscription } = input;

  if (billingRegime === MODULAR_BILLING_REGIME) {
    return true;
  }
  if (billingRegime != null) {
    // Any explicit non-modular regime is authoritative.
    return false;
  }

  const hasLiveModularSubscription = subscriptions.some(
    (subscription) =>
      isLiveSubscriptionStatus(subscription.status) &&
      subscription.version === MODULAR_CATALOG_VERSION
  );
  if (hasLiveModularSubscription) {
    return true;
  }

  const hasInFlightLegacyBilling =
    (hasActiveLegacySubscription ?? false) ||
    subscriptions.some(
      (subscription) =>
        isInFlightSubscriptionStatus(subscription.status) &&
        subscription.version !== MODULAR_CATALOG_VERSION
    );
  return !hasInFlightLegacyBilling;
}

/**
 * Stripe statuses on a shopify-provider bridge that mean our out-of-band
 * settlement has not caught up with the invoice yet, not that the merchant
 * failed to pay: `incomplete` is the bridge's first invoice, `past_due` a
 * renewal. Shopify is the payment authority — real non-payment surfaces as the
 * AppSubscription leaving ACTIVE, which cancels the bridge — and Stripe expires
 * a bridge that never settles into the terminal `incomplete_expired`, so the
 * window is self-limiting. `satisfies` guards against status-id typos.
 */
const SETTLING_SHOPIFY_BRIDGE_STATUSES = [
  "past_due",
  "incomplete",
] as const satisfies readonly StripeSubscriptionStatus[];

/**
 * True when a subscription is a Shopify bridge waiting on out-of-band
 * settlement. Reading such a bridge as dead locks out a merchant who has
 * already bought — and, on the billing surface, re-offers a plan they own.
 */
export function isSettlingShopifyBridge(sub: {
  status: string | null | undefined;
  billingProvider: string | null | undefined;
}): boolean {
  return (
    sub.billingProvider === "shopify" &&
    (SETTLING_SHOPIFY_BRIDGE_STATUSES as readonly string[]).includes(
      sub.status ?? ""
    )
  );
}

/**
 * True when a subscription currently grants the org what it bought: it is Live,
 * or it is a Shopify bridge still settling. Broader than
 * `isLiveSubscriptionStatus`, which answers only "is Stripe billing this now".
 */
export function isAccessGrantingSubscription(sub: {
  status: string | null | undefined;
  billingProvider: string | null | undefined;
}): boolean {
  return isLiveSubscriptionStatus(sub.status) || isSettlingShopifyBridge(sub);
}

/**
 * Declaration order IS the precedence, most actionable first
 */
export const GrantsNothingReasonList = [
  "stripe_dunning",
  "billing_unapproved",
  "subscription_not_live",
] as const;
export type GrantsNothingReason = (typeof GrantsNothingReasonList)[number];

/**
 * The one definition of when a subscription grants the org nothing, and why;
 * null means it grants normally.
 */
export function deriveGrantsNothingReason(sub: {
  status: string | null | undefined;
  billingProvider: string | null | undefined;
  billingApproved: boolean | null | undefined;
}): GrantsNothingReason | null {
  const isLive =
    sub.status === "active" ||
    sub.status === "trialing" ||
    isSettlingShopifyBridge(sub);

  if (!isLive) {
    return sub.status === "past_due"
      ? "stripe_dunning"
      : "subscription_not_live";
  }

  if (sub.status !== "trialing" && !sub.billingApproved) {
    return "billing_unapproved";
  }

  return null;
}

/**
 * Returns true when a cached subscription represents a directly-mutable Stripe
 * target: it must be Live (status in active/trialing/past_due) AND billed by
 * Stripe. A bridge Stripe sub (billingProvider="shopify") is Shopify-billed and
 * MUST NOT be treated as a mutable Stripe target.
 */
export function isMutableStripeTarget(sub: {
  status: string | null | undefined;
  billingProvider: string | null | undefined;
}): boolean {
  return (
    isLiveSubscriptionStatus(sub.status) && sub.billingProvider !== "shopify"
  );
}

type ModularStripeTargetCandidate = {
  status: string | null | undefined;
  version?: string | null;
  metadata?: { billingProvider?: string | null } | null;
};

/**
 * Positive evidence that an org has migrated onto a genuine modular Stripe
 * subscription of its own: a mutable target on the modular catalog. The
 * ADR-0007 non-collecting bridge carries the same catalog version, so a
 * regime-only check would match it and read every healthy Shopify subscriber as
 * migrated; `isMutableStripeTarget`'s billingProvider exclusion is what keeps
 * the bridge out.
 *
 * Every lane that must tell "this org is Stripe-billed now" from "this org is
 * still on its Shopify bridge" reads through here, because those lanes make
 * opposite, money-affecting choices on the same Shopify signal and must not
 * drift apart.
 */
export function hasMutableModularStripeTarget(
  subscriptions: readonly ModularStripeTargetCandidate[]
): boolean {
  return subscriptions.some(
    (subscription) =>
      subscription.version === MODULAR_CATALOG_VERSION &&
      isMutableStripeTarget({
        status: subscription.status,
        billingProvider: subscription.metadata?.billingProvider,
      })
  );
}

type PrimaryStripeSubscriptionCandidate = {
  status: string | null | undefined;
  stripeSubscriptionId?: string | null;
  metadata?: { billingProvider?: string | null } | null;
};

/**
 * Picks the single subscription that Stripe mutations should target when an
 * org carries more than one live-status sub (e.g. an active sub plus a
 * leftover past_due one). Selection must be identical everywhere facts and
 * refs are derived, or a plan can diff items from one sub and mutate another:
 * mutable Stripe targets only (bridge subs excluded), highest
 * LIVE_SUBSCRIPTION_STATUSES priority wins, ties broken by
 * stripeSubscriptionId so the result is deterministic regardless of source
 * ordering (DynamoDB GSI order is not).
 */
export function selectPrimaryMutableStripeSubscription<
  T extends PrimaryStripeSubscriptionCandidate,
>(subscriptions: readonly T[]): T | undefined {
  const mutable = subscriptions
    .filter((sub) =>
      isMutableStripeTarget({
        status: sub.status,
        billingProvider: sub.metadata?.billingProvider,
      })
    )
    .sort((a, b) =>
      (a.stripeSubscriptionId ?? "").localeCompare(b.stripeSubscriptionId ?? "")
    );
  for (const status of LIVE_SUBSCRIPTION_STATUSES) {
    const primary = mutable.find((sub) => sub.status === status);
    if (primary) {
      return primary;
    }
  }
  return undefined;
}

/**
 * Price metadata key a Trial Price carries pointing at its Module Price. A
 * trialing subscription item bills the $0 Trial Price, so `item.price.id` no
 * longer names the module; this key is the only link back.
 */
export const TRIAL_PRICE_FOR_METADATA_KEY = "trial_price_for";

export type SubscriptionItemPriceLike = {
  price: string | { id: string; metadata?: Record<string, unknown> | null };
};

/**
 * The Module Price a subscription item is identified by: the price the item
 * converts to at trial end when it bills a Trial Price, otherwise its own price.
 * Use this wherever "which module is this item" is the question; read the item
 * price directly only for the live billed amount.
 */
export function getItemModulePriceId(item: SubscriptionItemPriceLike): string {
  if (typeof item.price === "string") {
    return item.price;
  }
  const trialPriceFor = item.price.metadata?.[TRIAL_PRICE_FOR_METADATA_KEY];
  return typeof trialPriceFor === "string" && trialPriceFor !== ""
    ? trialPriceFor
    : item.price.id;
}

export function isTrialPrice(price: {
  metadata?: Record<string, unknown> | null;
}): boolean {
  const trialPriceFor = price.metadata?.[TRIAL_PRICE_FOR_METADATA_KEY];
  return typeof trialPriceFor === "string" && trialPriceFor !== "";
}

type CachedSubscriptionItemLike = {
  price?: SubscriptionItemPriceLike["price"] | null;
};

function toItemPriceId(item: CachedSubscriptionItemLike): string | null {
  if (!item.price) {
    return null;
  }
  return getItemModulePriceId({ price: item.price });
}

/**
 * Resolves every price id carried by a cached Stripe subscription. The cached
 * sub's top-level `stripePriceId` is only `items.data[0].price` — one
 * representative price — so reading it alone silently drops the other modules
 * of a multi-item modular sub. Prefer the live line items, fall back to the
 * phase-or-line `items` projection (schedule-managed subs), and only then to
 * the representative price (projections written before item-level data
 * existed).
 */
export function getCachedSubscriptionPriceIds(subscription: {
  liveItems?: CachedSubscriptionItemLike[] | null;
  items?: CachedSubscriptionItemLike[] | null;
  stripePriceId: string;
}): string[] {
  const fromLiveItems = (subscription.liveItems ?? [])
    .map(toItemPriceId)
    .filter((priceId): priceId is string => !!priceId);
  if (fromLiveItems.length > 0) {
    return [...new Set(fromLiveItems)];
  }

  const fromItems = (subscription.items ?? [])
    .map(toItemPriceId)
    .filter((priceId): priceId is string => !!priceId);
  if (fromItems.length > 0) {
    return [...new Set(fromItems)];
  }

  return subscription.stripePriceId ? [subscription.stripePriceId] : [];
}

// An item joining a live subscription must bill the same volume tier as its
// siblings, so the sub's own billed quantity wins over the org's live order
// metric — the two diverge when an admin pinned the tier at create time.
// Actively trialing items are excluded: their quantity is a grant-time copy,
// not the tier authority. Returns null when the items state no unanimous
// positive billed quantity.
export function deriveUnanimousBilledQuantity(
  items: Array<{
    quantity?: number | null;
    trialEndDate?: number | null;
  }>
): number | null {
  const billedQuantities = new Set(
    items
      .filter((item) => item.trialEndDate == null)
      .map((item) => item.quantity)
      .filter(
        (quantity): quantity is number =>
          typeof quantity === "number" && quantity > 0
      )
  );
  return billedQuantities.size === 1 ? [...billedQuantities][0]! : null;
}

export const OrganizationSubscriptionDtoSchema = z.object({
  id: z.uuid(),
  subscriptionId: z.uuid(),
  organizationId: z.uuid(),
  subscriptionPriceUsd: z.number(),
  subscriptionStatus: z.enum(SubscriptionStatusList),
  subscriptionInterval: z.enum(SubscriptionIntervalList),
  intervalCount: z.number().default(1),
  subscription: SubscriptionWithScopesDtoSchema,
  billingProvider: z.enum(SubscriptionBillingProviderList),
});

export type OrganizationSubscriptionDto = z.infer<
  typeof OrganizationSubscriptionDtoSchema
>;
